import './ComparableChart.css'

import PropTypes from 'prop-types'
import React from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { hexToRGBA } from 'utils'

import ChartTooltip from './ChartTooltip'
import { BAR_RADIUS, BAR_SIZE, DEFAULT_COLOR, DEFAULT_OPACITY } from './constants'
import LabelContent from './LabelContent'
import XAxisTick from './XAxisTick'

Label.propTypes = {
  ...Label.propTypes,
  value: PropTypes.object,
}

const ComparableChart = ({
  barLabelFormatter,
  barLabelVisible,
  data,
  height,
  margin,
  referenceLineValue,
  tooltipHeaderFormatter,
  tooltipLabelFormatter,
  width,
  yAxisLabelFormatter,
  yAxisVisible,
}) => {
  const parsedData = data.map(({ color, compared, current, ...item }) => ({
    ...item,
    compared,
    comparedLabel: barLabelFormatter(compared),
    current,
    currentLabel: barLabelFormatter(current),
    color: color || DEFAULT_COLOR,
  }))

  return (
    <div
      className="comparable-chart"
      style={{
        height,
        width,
      }}
    >
      <ResponsiveContainer>
        <BarChart
          barGap={1}
          data={parsedData}
          margin={{
            bottom: 5 + margin.bottom,
            left: -25 + margin.left,
            right: 5 + margin.right,
            top: 10 + margin.top,
          }}
        >
          <Tooltip
            content={
              <ChartTooltip
                data={parsedData}
                tooltipHeaderFormatter={tooltipHeaderFormatter}
                tooltipLabelFormatter={tooltipLabelFormatter}
              />
            }
          />

          {yAxisVisible && <YAxis stroke="#f8f8f8" tickCount={4} tickFormatter={yAxisLabelFormatter} />}

          <XAxis stroke="#f8f8f8" tick={<XAxisTick data={parsedData} />} />

          <CartesianGrid stroke="#f8f8f8" vertical={false} />

          {typeof referenceLineValue === 'number' && <ReferenceLine y={referenceLineValue} />}

          <Bar barSize={BAR_SIZE} dataKey="current" isAnimationActive={false} radius={BAR_RADIUS}>
            {parsedData.map(({ color }, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Cell key={`cell-${index}`} fill={color} />
            ))}
            {barLabelVisible && <LabelList content={LabelContent} valueAccessor={props => props} />}
          </Bar>

          <Bar barSize={BAR_SIZE} dataKey="compared" isAnimationActive={false} radius={BAR_RADIUS}>
            {parsedData.map(({ color }, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Cell key={`cell-${index}`} fill={hexToRGBA(color, DEFAULT_OPACITY)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

ComparableChart.defaultProps = {
  barLabelFormatter: value => value,
  barLabelVisible: true,
  height: '100%',
  margin: {
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
  },
  tooltipHeaderFormatter: value => value,
  tooltipLabelFormatter: (value, dataKey) => `${dataKey}: ${value}`,
  width: '100%',
  yAxisLabelFormatter: value => value,
  yAxisVisible: true,
}

ComparableChart.propTypes = {
  barLabelFormatter: PropTypes.func,
  barLabelVisible: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      compared: PropTypes.number,
      current: PropTypes.number,
    }),
  ).isRequired,
  height: PropTypes.string,
  margin: PropTypes.shape({
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
    top: PropTypes.number,
  }),
  referenceLineValue: PropTypes.number,
  tooltipHeaderFormatter: PropTypes.func,
  tooltipLabelFormatter: PropTypes.func,
  width: PropTypes.string,
  yAxisLabelFormatter: PropTypes.func,
  yAxisVisible: PropTypes.bool,
}

export default ComparableChart
